import { loadScript } from 'dashboard/helper/DOMHelpers';

export const loadFacebookSdk = async () => {
  console.log('📥 [loadFacebookSdk] Starting to load Facebook SDK...');

  return new Promise(resolve => {
    if (document.getElementById('facebook-jssdk')) {
      console.log(
        '✅ [loadFacebookSdk] Facebook SDK script already exists in DOM'
      );
      resolve();
      return;
    }

    console.log('📝 [loadFacebookSdk] Creating Facebook SDK script element');
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      console.log(
        '✅ [loadFacebookSdk] Facebook SDK script loaded successfully'
      );
      resolve();
    };

    script.onerror = () => {
      console.error('❌ [loadFacebookSdk] Failed to load Facebook SDK script');
      resolve(); // Still resolve to prevent hanging
    };

    document.body.appendChild(script);
    console.log(
      '🔗 [loadFacebookSdk] Facebook SDK script appended to document body'
    );
  });
};

export const initializeFacebook = (appId, apiVersion) => {
  const version = apiVersion || 'v22.0';
  return new Promise(resolve => {
    const init = () => {
      if (!window.FB) {
        setTimeout(init, 100); // Wait and retry if FB is not available
        return;
      }
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version,
      });
      resolve(window.FB);
    };

    if (window.FB) {
      init();
    } else {
      window.fbAsyncInit = init;
    }
  });
};

export const isValidBusinessData = businessData => {
  console.log(
    '🔐 [isValidBusinessData] Validating business data:',
    businessData
  );

  if (!businessData) {
    console.error(
      '❌ [isValidBusinessData] Business data is null or undefined'
    );
    return false;
  }

  const hasBusinessId = !!businessData.business_id;
  const hasWabaId = !!businessData.waba_id;
  const hasPhoneNumberId = !!businessData.phone_number_id;

  console.log('✓ [isValidBusinessData] Field check:', {
    business_id: businessData.business_id ? 'PRESENT' : 'MISSING',
    waba_id: businessData.waba_id ? 'PRESENT' : 'MISSING',
    phone_number_id: businessData.phone_number_id ? 'PRESENT' : 'MISSING',
  });

  const isValid = hasBusinessId && hasWabaId;
  console.log(
    isValid
      ? '✅ [isValidBusinessData] Valid'
      : '❌ [isValidBusinessData] Invalid'
  );

  return isValid;
};

export const createMessageHandler = onEmbeddedSignupData => {
  return event => {
    console.log(
      '📨 [createMessageHandler] Message event received from:',
      event.origin
    );

    // Validate origin
    if (!event.origin.endsWith('facebook.com')) {
      console.log(
        '⚠️  [createMessageHandler] Ignoring message from non-Facebook origin:',
        event.origin
      );
      return;
    }
    console.log('✅ [createMessageHandler] Origin is from facebook.com');

    try {
      let data;
      if (typeof event.data === 'string') {
        console.log('🔄 [createMessageHandler] Parsing string data...');
        try {
          data = JSON.parse(event.data);
        } catch (parseError) {
          // Facebook sends many non-JSON messages, silently ignore them
          console.log(
            '⏭️  [createMessageHandler] Skipping non-JSON message:',
            event.data.substring(0, 50) + '...'
          );
          return;
        }
      } else if (typeof event.data === 'object' && event.data !== null) {
        console.log('📦 [createMessageHandler] Using object data directly');
        data = event.data;
      } else {
        console.log(
          '⚠️  [createMessageHandler] Invalid data type:',
          typeof event.data
        );
        return;
      }

      console.log('📋 [createMessageHandler] Parsed data:', data);

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        console.log(
          '✅ [createMessageHandler] WA_EMBEDDED_SIGNUP message recognized. Processing...'
        );
        onEmbeddedSignupData(data);
      } else {
        console.log(
          '⚠️  [createMessageHandler] Unknown message type:',
          data.type
        );
      }
    } catch (error) {
      console.error('❌ [createMessageHandler] Unexpected error:', error);
      // Ignore errors for irrelevant messages
    }
  };
};

export const initWhatsAppEmbeddedSignup = configId => {
  console.log(
    '🔑 [initWhatsAppEmbeddedSignup] Starting with config ID:',
    configId
  );

  return new Promise((resolve, reject) => {
    if (!window.FB) {
      console.error(
        '❌ [initWhatsAppEmbeddedSignup] Facebook SDK not initialized'
      );
      reject(new Error('Facebook SDK not initialized'));
      return;
    }

    console.log('✅ [initWhatsAppEmbeddedSignup] Facebook SDK is available');

    window.FB.login(
      response => {
        console.log(
          '📲 [initWhatsAppEmbeddedSignup] FB.login callback received:',
          {
            hasAuthResponse: !!response.authResponse,
            hasError: !!response.error,
            hasCode: response.authResponse?.code ? 'YES' : 'NO',
          }
        );

        if (response.authResponse && response.authResponse.code) {
          console.log(
            '🔐 [initWhatsAppEmbeddedSignup] Auth code received, resolving promise'
          );
          resolve(response.authResponse.code);
        } else if (response.error) {
          console.error(
            '❌ [initWhatsAppEmbeddedSignup] Error in FB.login:',
            response.error
          );
          reject(new Error(response.error));
        } else {
          console.log(
            '⛔ [initWhatsAppEmbeddedSignup] Login was cancelled by user'
          );
          reject(new Error('Login cancelled'));
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      }
    );

    console.log(
      '📞 [initWhatsAppEmbeddedSignup] FB.login called, waiting for callback...'
    );
  });
};

export const setupFacebookSdk = async (appId, apiVersion) => {
  console.log('🚀 [setupFacebookSdk] Initializing with:', {
    appId,
    apiVersion: apiVersion || 'v22.0',
  });

  if (!appId) {
    console.error('❌ [setupFacebookSdk] Facebook App ID is required');
    throw new Error('Facebook App ID is required');
  }

  // SDK already loaded — re-init with current config and return immediately.
  // Do NOT go through fbAsyncInit here: the script has already run and
  // fbAsyncInit will never be called again by the SDK, so setting it would
  // create a Promise that never resolves.
  if (window.FB) {
    console.log(
      '✅ [setupFacebookSdk] FB already loaded, re-initializing and returning'
    );
    window.FB.init({
      appId,
      version: apiVersion || 'v22.0',
      xfbml: true,
      autoLogAppEvents: true,
    });
    console.log('✅ [setupFacebookSdk] FB.init() completed');
    return window.FB;
  }

  // SDK not yet loaded — set fbAsyncInit so the SDK calls it once the script
  // executes, then inject the script tag.
  return new Promise(resolve => {
    window.fbAsyncInit = function () {
      console.log(
        '📦 [setupFacebookSdk] fbAsyncInit called, initializing FB...'
      );
      window.FB.init({
        appId,
        version: apiVersion || 'v22.0',
        xfbml: true,
        autoLogAppEvents: true,
      });
      console.log('✅ [setupFacebookSdk] FB.init() completed');
      resolve(window.FB);
    };

    console.log(
      '📥 [setupFacebookSdk] FB not loaded yet, loading Facebook SDK script...'
    );
    loadFacebookSdk().then(() => {
      console.log('✅ [setupFacebookSdk] Facebook SDK script loaded');
    });
  });
};
